import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { createCustomStateSetOnUrlHandler } from '@appState/helpers';
import { NewsTopic, NewsTopicUpdateRequest } from '@models/marketing/news-topics/news-topic.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { FileStorageService, NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { getAll, StateDictionary } from 'src/app/app-state/helpers/store-base';
import { NewsTopicsService } from './news-topics.service';

type NewsTopicsState = {
  topics: NewsTopic[];
  isLoading: boolean;
  selectedTopicId: string | null;
  isUpdateLoading: boolean;
};

const initialState: NewsTopicsState = {
  topics: [],
  isLoading: false,
  selectedTopicId: null,
  isUpdateLoading: false,
};

const newsTopicsDictionary: StateDictionary<NewsTopic> = {
  entityList: 'topics',
  listLoading: 'isLoading',
  selectedEntity: '',
  selectedEntityLoading: 'isUpdateLoading',
  entityId: 'id',
  createLoading: '',
};

export const NewsTopicsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('news-topics-store'),
  withComputed(store => ({
    selectedTopic: computed(() => store.topics().find(topic => topic.id === store.selectedTopicId()) || null),
  })),
  withMethods(
    (
      store,
      newsTopicsService = inject(NewsTopicsService),
      fileStorageService = inject(FileStorageService),
      notificationsService = inject(NotificationsService),
    ) => {
      createCustomStateSetOnUrlHandler<NewsTopicsState>('/marketing/topics', {
        selectedTopicId: null,
      })(store);

      const repositoryMethods = {
        getAll: getAll<NewsTopic>({
          store,
          service: newsTopicsService,
          dictionary: newsTopicsDictionary,
        }),

        update: rxMethod<{ id: string; dto: NewsTopicUpdateRequest; oldFileId?: string | null }>(
          pipe(
            tap(() => patchState(store, { isUpdateLoading: true })),
            switchMap(entity =>
              newsTopicsService.update(entity.id, entity.dto).pipe(
                tap(() => {
                  patchState(store, {
                    topics: store.topics().map(topic => (topic.id === entity.id ? { ...topic, ...entity.dto } : topic)),
                    isUpdateLoading: false,
                  });
                  if (entity.oldFileId) {
                    repositoryMethods.deleteFile({ id: entity.oldFileId });
                  }
                }),
                catchError(() => {
                  patchState(store, { isUpdateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        activateFileAndUpdate: rxMethod<{
          file: { newId: string; oldId: string | null };
          entity: { id: string; dto: NewsTopicUpdateRequest };
        }>(
          pipe(
            tap(() => patchState(store, { isUpdateLoading: true })),
            switchMap(({ file, entity }) =>
              fileStorageService.activateFile(file.newId).pipe(
                tap(() => {
                  patchState(store, {
                    isUpdateLoading: false,
                  });

                  repositoryMethods.update({ ...entity, oldFileId: file.oldId ?? null });
                }),
                catchError(() => {
                  notificationsService.showNotification('Failed to upload file, please try again.');
                  patchState(store, { isUpdateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
        deleteFile: rxMethod<{ id: string }>(
          pipe(
            tap(() => patchState(store, { isUpdateLoading: true })),
            switchMap(({ id }) =>
              fileStorageService.deleteFile(id).pipe(
                tap(() => {
                  patchState(store, {
                    isUpdateLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isUpdateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationMethods = {
        selectTopic: (id: string) => {
          patchState(store, { selectedTopicId: id });
        },
      };
      return {
        ...repositoryMethods,
        ...navigationMethods,
      };
    },
  ),
);

export interface NewsTopicsStoreType {
  readonly topics: Signal<NewsTopic[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedTopicId: Signal<string | null>;
  readonly isUpdateLoading: Signal<boolean>;

  readonly selectedTopic: Signal<NewsTopic | null>;

  readonly getAll: () => void;
  readonly update: (input: { id: string; dto: NewsTopicUpdateRequest; oldFileId?: string | null }) => void;
  readonly activateFileAndUpdate: (input: {
    file: { newId: string; oldId: string | null };
    entity: { id: string; dto: NewsTopicUpdateRequest };
  }) => void;
  readonly deleteFile: (input: { id: string }) => void;
  readonly selectTopic: (id: string) => void;
}
